import {
  getServiceInformationTemplates,
  updateServiceInformationTemplate,
} from '@/api/serviceInformationTemplates'
import {
  caseFuneralHomeAtom,
  caseSelector,
  dateFormatAtom,
  selectedThemeAtom,
  themeAtom,
  themeOverrideAtom,
} from '@/atoms'
import {
  pageSetLocaleAtom,
  serviceInformationTemplatesAtom,
} from '@/atoms/_editorAtoms'
import { Dropdown, TinyMCE } from '@/components/global'
import { Button, Input } from '@/components/priority'
import Flex from '@/components/priority/Flex/Flex'
import Text from '@/components/priority/Text/Text'
import { useSetToast } from '@/hooks'
import { useErrorHandler } from '@/hooks/utility/useErrorHandler'
import {
  GetGlobalServiceInformationTemplatesNormalized,
  SERVICE_TEMPLATE_CATEGORY_ORDER,
} from '@messenger/types'
import type { ServiceTemplateCategory } from '@messenger/types'
import { useRef, useState } from 'react'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import * as S from '../InsertMenu/SlideoutMenu/SlideoutMenu.styled'
import { mergedBoxHtml } from '@/utils/tinyMCE/updateDynamicText/mergedBoxHtml'
import { useTranslation } from 'react-i18next'
import {
  DARK_PREVIEW_BG,
  LIGHT_PREVIEW_BG,
  BackgroundToggle,
} from './ServiceTemplates.styled'
import Icon from '@/components/priority/Icon/Icon'

type ServiceTemplateEditProps = {
  setEditingTemplate: (editingTemplate: boolean) => void
  template: GetGlobalServiceInformationTemplatesNormalized | null
}

const ServiceTemplateEdit = ({
  setEditingTemplate,
  template,
}: ServiceTemplateEditProps) => {
  const { t } = useTranslation()
  const setToast = useSetToast()
  const handleError = useErrorHandler('ServiceTemplateEdit')
  const editorRef = useRef<Editor | null>(null)

  const caseDetails = useRecoilValue(caseSelector)
  const themeOverride = useRecoilValue(themeOverrideAtom) as {
    product_id: string
  }
  const funeralHomeDetails = useRecoilValue(caseFuneralHomeAtom)
  const dateFormat = useRecoilValue(dateFormatAtom)
  const pageSetLocale = useRecoilValue(pageSetLocaleAtom)
  const selectedTheme = useRecoilValue(selectedThemeAtom)

  const setServiceInformationTemplates = useSetRecoilState(
    serviceInformationTemplatesAtom,
  )

  const theme = useRecoilValue(themeAtom)
  const isDarkTheme = theme === 'dark'
  const [invertedBg, setInvertedBg] = useState(false)
  const isDarkBg = isDarkTheme ? !invertedBg : invertedBg

  const [isUpdating, setIsUpdating] = useState(false)
  const [templateName, setTemplateName] = useState(template?.name || '')
  const [templateCategory, setTemplateCategory] =
    useState<ServiceTemplateCategory>(
      (template?.category as ServiceTemplateCategory) ?? 'DEFAULT',
    )

  const categoryItems = Object.fromEntries(
    SERVICE_TEMPLATE_CATEGORY_ORDER.map((cat) => [
      cat,
      t(`editor.serviceTemplates.categories.${cat}`),
    ]),
  )

  const setTinyMCERef = (ref: Editor | null) => {
    if (!ref) return
    editorRef.current = ref
  }

  const handleUpdateTemplate = async () => {
    const content = editorRef.current?.getContent?.()

    if (!templateName.trim()) {
      handleError(
        t('editor.serviceTemplates.notifications.enterName'),
        new Error('Please enter a template name'),
      )
      return
    }

    if (!content || !content.trim() || content === '<p>&nbsp;</p>') {
      handleError(
        t('editor.serviceTemplates.notifications.enterContent'),
        new Error('Please add content to your template'),
      )
      return
    }

    if (!template?.id) {
      handleError(
        t('editor.serviceTemplates.notifications.templateIdNotFound'),
        new Error('Template ID not found'),
      )
      return
    }

    const productId = themeOverride?.product_id
    if (!productId) {
      handleError(
        t('editor.serviceTemplates.notifications.productIdNotFound'),
        new Error('Product ID not found'),
      )
      return
    }

    setIsUpdating(true)

    try {
      await updateServiceInformationTemplate(template.id, {
        template: content,
        name: templateName,
        category: templateCategory,
      })

      const serviceInformationTemplatesData =
        await getServiceInformationTemplates(productId)

      setServiceInformationTemplates(serviceInformationTemplatesData.data)

      setToast({
        text: t('editor.serviceTemplates.notifications.templateUpdated'),
        icon: 'success',
      })

      setEditingTemplate(false)
    } catch (error) {
      const err = error as {
        response?: { data?: { message?: string } }
        message?: string
      }
      handleError(
        err?.response?.data?.message ||
          err?.message ||
          t('editor.serviceTemplates.notifications.failedToUpdateTemplate'),
        error as Error,
      )
    } finally {
      setIsUpdating(false)
    }
  }

  const getTemplateContent = (content: string) => {
    return mergedBoxHtml(content, {
      caseDetails,
      funeralHomeDetails,
      dateFormat,
      pageSetLocale,
      environment: 'editor',
      selectedTheme,
    })
  }

  return (
    <Flex column width="100%" height="100%">
      {/* @ts-expect-error - overflowY/overflowX are valid props in styled component */}
      <S.Content column flex="1" overflowY="auto" overflowX="hidden">
        <Flex column padding="0 32px 32px 32px" gap="20px">
          <Flex column gap="8px">
            <Input
              // @ts-expect-error - Input component value prop not properly typed
              value={[templateName, setTemplateName]}
              placeholder={t('editor.serviceTemplates.enterTemplateName')}
              label={t('editor.serviceTemplates.templateNameLabel')}
            />
          </Flex>
          <Flex column gap="8px">
            <Dropdown
              full
              width="100%"
              label={t('editor.serviceTemplates.categoryLabel')}
              // @ts-expect-error - Dropdown selected prop not properly typed
              selected={[templateCategory, setTemplateCategory]}
              items={categoryItems}
              useKeyAsValue
            />
          </Flex>
          <Flex column gap="8px">
            <Flex justify="space-between" align="center">
              <Text size="14px" weight="600">
                {t('editor.serviceTemplates.templateContentLabel')}
              </Text>
              <BackgroundToggle
                $active={isDarkBg}
                onClick={() => setInvertedBg((prev) => !prev)}
                title={t('editor.serviceTemplates.invertBackground')}
              >
                <Icon icon={isDarkBg ? 'moon' : 'sun'} />
                {isDarkBg
                  ? t('editor.serviceTemplates.darkBackground')
                  : t('editor.serviceTemplates.lightBackground')}
              </BackgroundToggle>
            </Flex>
            <Text size="12px" color="gray2">
              {t('editor.serviceTemplates.updateTemplateDescription')}
            </Text>
          </Flex>
          <Flex
            column
            border="1px solid"
            borderColor="gray4"
            padding="16px"
            minHeight="300px"
            transition="background 0.2s ease"
            background={isDarkBg ? DARK_PREVIEW_BG : LIGHT_PREVIEW_BG}
            style={{
              borderRadius: '4px',
            }}
          >
            <TinyMCE
              setRef={setTinyMCERef}
              caseDetails={caseDetails}
              value={getTemplateContent(template?.template || '')}
              init={{
                height: 300,
                menubar: false,
                statusbar: false,
              }}
            />
          </Flex>
          <Flex justify="flex-end" gap="12px" margin="20px 0 0 0">
            <Button onClick={() => setEditingTemplate(false)}>
              {t('editor.serviceTemplates.cancelButton')}
            </Button>
            <Button
              primary
              onClick={handleUpdateTemplate}
              disabled={isUpdating}
            >
              {isUpdating
                ? t('editor.serviceTemplates.updatingButton')
                : t('editor.serviceTemplates.updateTemplateButton')}
            </Button>
          </Flex>
        </Flex>
      </S.Content>
    </Flex>
  )
}

export default ServiceTemplateEdit
